import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Info, Check, X, ExternalLink } from "lucide-react";
import ProcessingIndicator from "./ProcessingIndicator";
import FileUploader, { FileUploadButton } from "./FileUploader";
import "./Dashboard.css";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { getMaxFilesPerQuery } from "../../utils/subscriptionAccess";
import ConfirmDialog from "../ConfirmDialog";

export default function QueryInput({
  query,
  setQuery,
  onSubmit,
  isLoading = false,
  disabled = false,
  files = null, // Renamed from file to files, expect array
  fileUploading = false,
  onFileUpload,
  onFileRemove,
  placeholder = "Ask any legal question or describe an issue — responses follow IRAC legal analysis.",
  submitDisabled = false,
  inputDisabled = false,
  multipleFiles = true,
}) {
  const { user, isAuthenticated } = useAuth();
  const isLoggedIn = !!isAuthenticated;
  const { darkMode } = useTheme();
  const [showPreRunConfirm, setShowPreRunConfirm] = React.useState(false);

  const maxAllowedFiles = getMaxFilesPerQuery(user);
  const currentFilesCount = Array.isArray(files) ? files.length : files ? 1 : 0;
  const isFileUploaderDisabled = currentFilesCount >= maxAllowedFiles;

  const fileUploadTooltip = isFileUploaderDisabled
    ? maxAllowedFiles === 1
      ? "Only 1 document per query is allowed on free plans. Upgrade to upload multiple documents."
      : `Maximum of ${maxAllowedFiles} document${maxAllowedFiles > 1 ? "s" : ""} allowed for your plan.`
    : undefined;

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  // Requirements: at least 8 chars, at least one letter, not punctuation only
  const trimmed = (query || "").trim();
  const hasMinLength = trimmed.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(trimmed);
  const isNotPunctuationOnly = /[a-zA-Z0-9]/.test(trimmed);
  const isQueryValid = hasMinLength && hasLetter && isNotPunctuationOnly;

  const hasFiles = Array.isArray(files) ? files.length > 0 : !!files;
  const isInputInvalid = !isQueryValid && !hasFiles;
  const validationMessage =
    "Please Enter Valid Query";
  const [submitHover, setSubmitHover] = React.useState(false);
  const showValidationTooltip = isInputInvalid && submitHover;

  // Auto-resize textarea
  useEffect(() => {
    const adjustHeight = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        const scrollHeight = textareaRef.current.scrollHeight;
        // Cap the grow so a long query cannot crowd out the responses below.
        // Past this the textarea scrolls internally (overflow-y is auto in CSS).
        // Viewport-relative so short laptops give up less of the screen.
        // NOTE: keep in sync with max-height on .query-textarea-slim.
        const maxH = Math.max(
          88,
          Math.min(120, Math.round(window.innerHeight * 0.14)),
        );
        textareaRef.current.style.height = `${Math.min(scrollHeight + 2, maxH)}px`;
      }
    };

    adjustHeight();
    const timeout = setTimeout(adjustHeight, 0);
    // The cap is viewport-relative, so recompute it when the window resizes.
    window.addEventListener("resize", adjustHeight);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", adjustHeight);
    };
  }, [query]);

  const handleSubmit = () => {
    if (submitDisabled || isLoading || isInputInvalid) return;
    if (!isLoggedIn) {
      setShowPreRunConfirm(true);
      return;
    }
    if (onSubmit) onSubmit();
  };

  const handleConfirmStartResearch = () => {
    setShowPreRunConfirm(false);
    if (onSubmit) onSubmit();
  };

  const handleCancelStartResearch = () => {
    setShowPreRunConfirm(false);
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isLoading && !fileUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isLoading || fileUploading || isFileUploaderDisabled)
      return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      let droppedFiles = Array.from(e.dataTransfer.files);
      const remainingSlots = maxAllowedFiles - currentFilesCount;
      if (remainingSlots <= 0) return;
      if (droppedFiles.length > remainingSlots) {
        droppedFiles = droppedFiles.slice(0, remainingSlots);
      }
      if (!multipleFiles && droppedFiles.length > 1) {
        droppedFiles = [droppedFiles[0]];
      }
      if (onFileUpload) {
        onFileUpload(droppedFiles);
      }
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
      <div className="query-section-wrapper">
        <motion.div
          className={`query-field-wrapper ${isDragOver ? "drag-over" : ""}${isInputInvalid ? " query-field-has-error" : ""}`}
          onClick={() => textareaRef.current?.focus()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          animate={
            isDragOver
              ? {
                scale: 1.01,
                borderColor: "#2563eb",
                boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.2)",
              }
              : {}
          }
        >
          <motion.textarea
            ref={textareaRef}
            placeholder={isDragOver ? "Drop files here..." : placeholder}
            className="query-textarea-slim"
            rows={1}
            value={query}
            onChange={handleQueryChange}
            disabled={disabled || isLoading}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          {/* Attach + submit live inside the field, as in ChatGPT/Claude, so
              the composer costs one row instead of two. */}
          <div className="query-field-actions">
            <FileUploadButton
              fileInputRef={fileInputRef}
              disabled={
                disabled || isLoading || isFileUploaderDisabled || fileUploading
              }
              onChange={onFileUpload}
              multiple={multipleFiles && maxAllowedFiles > 1}
              maxAllowed={maxAllowedFiles - currentFilesCount}
              tooltipText={fileUploadTooltip}
            />
            <div
              className="query-submit-inline-wrap query-submit-area-with-tooltip"
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
            >
              <motion.button
                type="button"
                className="query-submit-inline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmit();
                }}
                disabled={submitDisabled || isLoading || isInputInvalid}
                aria-label="Submit query"
                title="Submit query"
                whileHover={
                  submitDisabled || isLoading || isInputInvalid
                    ? {}
                    : { scale: 1.06 }
                }
                whileTap={
                  submitDisabled || isLoading || isInputInvalid
                    ? {}
                    : { scale: 0.94 }
                }
              >
                <Send size={16} />
              </motion.button>
              {showValidationTooltip && (
                <div className="query-validation-tooltip" role="tooltip">
                  <div className="query-validation-tooltip-tail" />
                  <div className="query-validation-tooltip-content">
                    <span className="query-validation-tooltip-icon">
                      <Info size={14} strokeWidth={2.5} />
                    </span>
                    <span className="query-validation-tooltip-text">
                      {validationMessage}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {!isLoggedIn && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "0.5rem 0",
            }}
          >
            <span
              className="query-submit-free-badge-mobile"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#e6f9f0",
                border: "1.5px solid #2dc76d",
                borderRadius: "999px",
                padding: "5px 14px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#1a8f4e",
              }}
            >
              <span style={{ fontSize: "13px" }}>&#10003;</span> Free &middot;
              2 AI models &middot; No login required
            </span>
          </div>
        )}

        {/* Bottom row: Processing (left), uploaded files (right) */}
        <div className="query-bottom-row">
          <div className="query-processing-left">
            <ProcessingIndicator isLoading={isLoading} />
          </div>
          <div className="query-file-right">
            <FileUploader
              files={files}
              isUploading={fileUploading}
              disabled={disabled || isLoading}
              onRemove={onFileRemove}
            />
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showPreRunConfirm}
        title="Start Free Research Mode?"
        message={
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <div
              style={{
                backgroundColor: darkMode
                  ? "rgba(37, 99, 235, 0.15)"
                  : "#DBEAFF",
                border: `1px solid ${darkMode ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
                borderRadius: "0.5rem",
                padding: "0.4rem 1rem 0.4rem 0.75rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Info
                size={20}
                color="#2563eb"
                style={{ flexShrink: 0 }}
              />
              <p
                style={{
                  margin: 0,
                  color: darkMode ? "#E2E8F0" : "#1E3A8A",
                  fontSize: "0.95rem",
                  lineHeight: "1.5",
                }}
              >
                <strong>Free Demo:</strong> This will run your{" "}
                <strong>Initial Query</strong> and Comparative Analysis using 2
                AI models.
              </p>
            </div>

            <div className="debate-free-demo-split">
              <div className="debate-free-demo-col">
                <h4
                  style={{
                    margin: "0 0 0.5rem 0",
                    fontSize: "0.95rem",
                    color: darkMode ? "#E2E8F0" : "#0F172A",
                  }}
                >
                  This demo includes:
                </h4>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {[
                    "One document upload (optional)",
                    "Initial Query with 2 AI models",
                    "Comparative Analysis with 1 AI model",
                  ].map((text, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.5rem",
                        fontSize: "0.9rem",
                        color: darkMode ? "#94A3B8" : "#475569",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          backgroundColor: "#10B981",
                          color: "white",
                          flexShrink: 0,
                          marginTop: "0.15rem",
                        }}
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="debate-free-demo-divider"></div>
              <div className="debate-free-demo-col-right">
                <h4
                  style={{
                    margin: "0 0 0.5rem 0",
                    fontSize: "0.95rem",
                    color: darkMode ? "#E2E8F0" : "#0F172A",
                  }}
                >
                  Not included in free demo:
                </h4>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {[
                    "Final Synthesis",
                    "Rerun comparisons with a different AI",
                    "All AI models access",
                  ].map((text, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.5rem",
                        fontSize: "0.9rem",
                        color: darkMode ? "#94A3B8" : "#475569",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          backgroundColor: "#EF4444",
                          color: "white",
                          flexShrink: 0,
                          marginTop: "0.15rem",
                        }}
                      >
                        <X size={12} strokeWidth={3} />
                      </span>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div
              style={{
                borderTop: `1px solid ${darkMode ? "#334155" : "#E2E8F0"}`,
                margin: "0.5rem 0",
              }}
            ></div>

            <p
              style={{
                margin: 0,
                fontSize: "0.9rem",
                color: darkMode ? "#94A3B8" : "#475569",
                lineHeight: "1.5",
              }}
            >
              To access all AI models, <strong>Final Synthesis</strong>, and
              re-runs, start a <strong>free trial</strong> or upgrade.
            </p>
            <div
              style={{
                borderTop: `1px solid ${darkMode ? "#334155" : "#E2E8F0"}`,
                margin: "0.5rem 0 0.25rem 0",
              }}
            ></div>

            <div style={{ marginTop: "0.25rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontSize: "0.95em",
                  fontWeight: 600,
                  color: darkMode ? "#e2e8f0" : "#1E3A8A",
                }}
              >
                Your Query:
              </label>
              <textarea
                value={query}
                onChange={(e) => {
                  handleQueryChange(e);
                  e.target.style.height = "auto";
                  e.target.style.height = e.target.scrollHeight + "px";
                }}
                ref={(el) => {
                  if (el) {
                    el.style.height = "auto";
                    el.style.height = el.scrollHeight + "px";
                  }
                }}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  padding: "0.4rem 1rem 0.4rem 0.75rem",
                  borderRadius: "0.5rem",
                  border: `1px solid ${darkMode ? "rgba(59, 130, 246, 0.3)" : "#BFDBFE"}`,
                  background: darkMode ? "rgba(37, 99, 235, 0.15)" : "#DBEAFF",
                  color: darkMode ? "#e2e8f0" : "#1e293b",
                  fontFamily: "inherit",
                  fontSize: "0.95rem",
                  resize: "none",
                  outline: "none",
                  overflow: "hidden",
                  transition: "border-color 0.2s",
                  display: "block",
                  fieldSizing: "content",
                  lineHeight: "1.5",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) =>
                (e.target.style.borderColor = darkMode
                  ? "rgba(59, 130, 246, 0.3)"
                  : "#BFDBFE")
                }
              />

              {/* ── Free Directory CTA ──
                  Opens the full directory form on /directory in a new tab so
                  this screen stays uncluttered. ── */}
              <div style={{ marginTop: "0.75rem" }}>
                <a
                  href="/directory"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.7rem 1rem",
                    background: darkMode ? "rgba(250,251,254,0.04)" : "#FAFBFE",
                    border: `1.5px dashed ${darkMode ? "rgba(172,208,255,0.35)" : "#acd0ff"}`,
                    borderRadius: "10px",
                    textDecoration: "none",
                  }}
                >
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      background: darkMode ? "rgba(219,232,255,0.15)" : "#dbe8ff",
                      color: darkMode ? "#93c5fd" : "#2347b2",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ExternalLink size={12} />
                  </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: darkMode ? "#93c5fd" : "#24458f" }}>
                    Want more visibility for your firm or practice? Join our free directory{" "}
                    <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#7a889d" }}>(optional)</span>
                  </span>
                </a>
              </div>
            </div>
          </div>
        }
        onConfirm={handleConfirmStartResearch}
        onCancel={handleCancelStartResearch}
        confirmText="Start Free Research"
        cancelText="Cancel"
        type="debate"
        requireConsent={!isLoggedIn}
      />
    </div>
  );
}
